import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLocalizations } from '@/hooks/useLocalizations';

export interface MessageDraft {
  id: string;
  user_id: string;
  draft_name: string | null;
  recipient_ids: string[];
  subject: string | null;
  message: string | null;
  service_type: string | null;
  location: string | null;
  budget_range: string | null;
  timeline: string | null;
  created_at: string;
  updated_at: string;
}

export interface DraftFormData {
  recipient_ids: string[];
  subject: string;
  message: string;
  service_type: string;
  location: string;
  budget_range: string;
  timeline: string;
}

export function useMessageDrafts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { lCommon } = useLocalizations();

  // Fetch all drafts
  const { data: drafts, isLoading } = useQuery({
    queryKey: ['message-drafts', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('opportunities_message_drafts')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data as MessageDraft[];
    },
    enabled: !!user?.id,
  });

  // Save or update draft
  const saveDraftMutation = useMutation({
    mutationFn: async ({
      draftId,
      draftName,
      formData,
    }: {
      draftId?: string;
      draftName?: string;
      formData: DraftFormData;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const draftData = {
        user_id: user.id,
        draft_name: draftName || null,
        recipient_ids: formData.recipient_ids,
        subject: formData.subject || null,
        message: formData.message || null,
        service_type: formData.service_type || null,
        location: formData.location || null,
        budget_range: formData.budget_range || null,
        timeline: formData.timeline || null,
      };

      if (draftId) {
        // Update existing draft
        const { data, error } = await supabase
          .from('opportunities_message_drafts')
          .update(draftData)
          .eq('id', draftId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new draft
        const { data, error } = await supabase
          .from('opportunities_message_drafts')
          .insert(draftData)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['message-drafts', user?.id],
      });
      
      if (!variables.draftId) {
        toast.success(lCommon('toast.draft_saved.success', { defaultValue: 'Draft saved' }));
      }
    },
    onError: () => {
      toast.error(lCommon('toast.failed_to_save_draft.error', { defaultValue: 'Failed to save draft' }));
    },
  });

  // Delete draft
  const deleteDraftMutation = useMutation({
    mutationFn: async (draftId: string) => {
      const { error } = await supabase
        .from('opportunities_message_drafts')
        .delete()
        .eq('id', draftId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['message-drafts', user?.id],
      });
      toast.success(lCommon('toast.draft_deleted.success', { defaultValue: 'Draft deleted' }));
    },
    onError: () => {
      toast.error(lCommon('toast.failed_to_delete_draft.error', { defaultValue: 'Failed to delete draft' }));
    },
  });

  // Get a draft by ID
  const getDraftById = (draftId: string): MessageDraft | undefined => {
    return drafts?.find(d => d.id === draftId);
  };

  return {
    drafts: drafts || [],
    isLoading,
    getDraftById,
    saveDraft: saveDraftMutation.mutate,
    saveDraftAsync: saveDraftMutation.mutateAsync,
    deleteDraft: deleteDraftMutation.mutate,
    isSaving: saveDraftMutation.isPending,
  };
}
